import { yupResolver } from '@hookform/resolvers/yup';
import { Add } from '@mui/icons-material';
import DatePicker from '@mui/lab/DatePicker';
import {
  Autocomplete,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  ListSubheader,
  MenuItem,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  TextField,
  Typography
} from '@mui/material';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import Select from '@mui/material/Select';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import AttachmentBox from '../../components/AttachmentBox/AttachmentBox';
import DetailsCard from '../../components/DetailsCard/DetailsCard';
import FormatCurrencyInput from '../../components/FormatNumber/FormatCurrencyInput';
import LocationInput from '../../components/LocationInput/LocationInput';
import { ProjectStatus } from '../../enums/projectStatus';
import { useFilesManagement } from '../../hooks/useFilesManagement';
import { FileType } from '../../models/fileType';
import { Project } from '../../models/project';
import { Talent } from '../../models/talent';
import { PROJECTS } from '../../router/constants';
import { fileService } from '../../services/fileService';
import { projectService } from '../../services/projectService';
import { talentService } from '../../services/talentService';
import ConfirmModal from './ConfirmModal';
import SuccessProjectModal from './SuccessProjectModal';
import PageTitle from '../../components/PageTitle/PageTitle';
import { removeEmptyObjectFields } from '../../utils/helpers';

const CreateProject = (): JSX.Element => {
  const navigate = useNavigate();
  const projectAttachmentRef = useRef<HTMLInputElement | null>(null);
  const [openBudget, setOpenBudget] = useState<boolean>(false);
  const { files, setFiles, onAttachFile, onEditFile, onDeleteFile } =
    useFilesManagement();
  const [attachments, setAttachments] = useState<FileType[]>([]);
  const [hasLocation, setHasLocation] = useState<string>('virtual');
  const [needExpire, setNeedExpire] = useState<boolean>(false);
  const [scheduleOption, setScheduleOption] = useState('noSpecific');
  const [talents, setTalents] = useState<Talent[]>([]);
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [project, setProject] = useState<Project | undefined>(undefined);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [modalData, setModalData] = useState<Project | null>(null);
  const [editing, setEditing] = useState(false);

  const { id } = useParams();
  const location = useLocation();

  const projectSchema = yup.object({}).shape({
    projectTitle: yup
      .string()
      .default('')
      .required('Project Title is required'),
    poNumber: yup.string().default(''),
    category: yup.string().default(''),
    address: yup.string().default(''),
    talent: yup
      .object()
      .default(null)
      .required('Talent is required')
      .typeError('Talent is required'),
    expenseCode: yup.string().default(''),
    description: yup.string().default(''),
    status: yup.string().default('PENDING'),
    expireDate: yup.date().default(null).nullable(),
    specialInstructions: yup.string().default(''),
    paymentType: yup
      .string()
      .default('FLAT_FEE')
      .required('Payment Type is required'),
    pricePerHour: yup
      .string()
      .default('')
      .typeError('Please enter an hourly rate')
      .when('paymentType', {
        is: 'HOURLY_RATE',
        then: (schema) => schema.required('Please enter an hourly rate')
      })
      .nullable(),
    budget: yup
      .string()
      .default('')
      .test('lowerThanOld', '', (value) => {
        if (project?.amountPaid && Number(project.amountPaid) >= 0 && value) {
          return Number(project.amountPaid) <= Number(value);
        } else {
          return true;
        }
      })
      .nullable(),
    startDate: yup.date().default(null).nullable(),
    endDate: yup.date().default(null).nullable()
  });

  interface CreateProjectForm extends yup.InferType<typeof projectSchema> {
    editReason: string;
  }

  const defaultValues = projectSchema.getDefault();

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    clearErrors,
    formState: { errors }
  } = useForm<CreateProjectForm>({
    defaultValues: defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(projectSchema)
  });

  const paymentType = watch('paymentType');
  const budget = watch('budget');
  const pricePerHour = watch('pricePerHour');
  const address = watch('address');
  const startDate = watch('startDate');
  const endDate = watch('endDate');

  useEffect(() => {
    setOpenBudget(!!project?.budget);
    setNeedExpire(!!project?.expireDate);
    setHasLocation(project?.address ? 'physical' : 'virtual');
    setScheduleOption(project?.startDate ? 'specific' : 'noSpecific');
    if (project?.attachments) {
      setAttachments(project?.attachments);
    }
  }, [project]);

  const getProject = () => {
    projectService.getProjectById(Number(id)).then((response) => {
      const data = removeEmptyObjectFields(response.data);
      setProject(response.data);
      reset(data);
    });
  };

  useEffect(() => {
    getTalents();
  }, []);

  useEffect(() => {
    if (id) {
      getProject();
      setEditing(location.pathname !== `/create-project/${id}`);
    } else {
      reset(defaultValues);
      setEditing(false);
    }
  }, [id]);

  const getTalents = () => {
    talentService
      .getTalentList({ params: { active: true } })
      .then((response) => {
        setTalents(response.data);
      });
  };
  const onSubmit = (data: CreateProjectForm) => {
    const output = JSON.parse(JSON.stringify(data));
    output.budget = output.budget === 0 ? null : output.budget;
    editing && project?.status !== 'DRAFT'
      ? updateProject(output)
      : createProject(output);
  };

  const submitButtonCondition = (projectStatus: keyof typeof ProjectStatus) => {
    switch (projectStatus) {
      case ProjectStatus.DRAFT:
        return 'Send Now';
      case ProjectStatus.DECLINED:
        return 'Resend';
      default:
        return 'Save';
    }
  };

  const updateProject = (project: Project) => {
    projectService.updateProject(project).then(() => {
      setModalData(project);
      if (files.length === 0) {
        setSuccessModalOpen(true);
      } else {
        uploadFiles(project.id);
      }
    });
  };

  const createProject = (project: Project) => {
    if (project.id && project.status !== 'DRAFT') {
      delete project.id;
    }
    projectService.createProject(project).then((response) => {
      setModalData(project);
      if (files.length === 0) {
        setSuccessModalOpen(true);
      } else {
        uploadFiles(response.data.id);
      }
    });
  };

  const uploadFiles = (projectID: number | undefined) => {
    if (projectID && files.length) {
      const formData: FormData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });
      projectService.saveProjectFiles(projectID, formData).then(() => {
        setSuccessModalOpen(true);
      });
    }
  };

  const onEditAttachment = (uuid: string, fileName: string) => {
    fileService.editFileName({ uuid, fileName }).then(() => {
      setAttachments((prevState) =>
        prevState.map((attachment) =>
          attachment.uuid === uuid ? { ...attachment, fileName } : attachment
        )
      );
    });
  };

  const onScheduleDateChange = (event: SelectChangeEvent) => {
    setScheduleOption(event.target.value);
    setValue('startDate', null);
    setValue('endDate', null);
    clearErrors(['startDate', 'endDate']);
  };

  const onDeleteAttachment = (fileToRemove: FileType) => {
    fileService.deleteFileById(fileToRemove.uuid).then(() => {
      const newAttachmentsArray = attachments.filter(
        (attach) => attach.uuid !== fileToRemove.uuid
      );
      setAttachments(newAttachmentsArray);
    });
  };

  const downloadAttachment = (uuid: string) => {
    fileService.getFileUrl(uuid).then((response) => {
      window.open(response.data, 'blank');
    });
  };

  const saveAsDraft = () => {
    projectService
      .createProject(getValues() as unknown as Project, {
        params: { isDraft: true }
      })
      .then(() => {
        navigate(PROJECTS);
      });
  };

  return (
    <>
      <PageTitle>{editing ? 'Edit Project' : 'Create Project'}</PageTitle>
      <DetailsCard title={editing ? `Project #${id ?? ''}` : 'New Project'}>
        <form>
          <Grid container spacing={4}>
            <Grid item container flexDirection="column" gap={3} xs={12} md={6}>
              <Controller
                name="poNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="PO #"
                    error={!!errors.poNumber}
                    {...field}
                  />
                )}
              />
              <Controller
                name="projectTitle"
                control={control}
                render={({ field }) => (
                  <TextField
                    error={!!errors.projectTitle}
                    label="Project Title *"
                    helperText={errors.projectTitle?.message}
                    inputProps={{
                      maxLength: 50
                    }}
                    {...field}
                  />
                )}
              />
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <TextField
                    id="category-basic"
                    label="Category"
                    error={!!errors.category}
                    inputProps={{
                      maxLength: 50
                    }}
                    {...field}
                  />
                )}
              />
              <Controller
                name="expenseCode"
                control={control}
                render={({ field }) => (
                  <TextField
                    id="expenseCode"
                    error={!!errors.expenseCode}
                    label="Expense Code"
                    inputProps={{
                      maxLength: 20
                    }}
                    {...field}
                  />
                )}
              />
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: '400', textTransform: 'uppercase' }}
              >
                Schedule Options
              </Typography>
              <FormControl fullWidth>
                <InputLabel id="scheduleOptionsLabel">
                  Schedule Options
                </InputLabel>
                <Select
                  labelId="scheduleOptionsLabel"
                  name="scheduleOption"
                  id="scheduleOption"
                  label="Schedule Options"
                  onChange={onScheduleDateChange}
                  value={scheduleOption}
                >
                  <ListSubheader
                    sx={{
                      borderBottom: '1px solid #DDDDDD ',
                      fontWeight: '400',
                      color: '#B3B3B3'
                    }}
                  >
                    SCHEDULE OPTIONS
                  </ListSubheader>

                  <MenuItem value={'specific'}>Specific Date</MenuItem>
                  <MenuItem value={'noSpecific'}>No Specific Date</MenuItem>
                </Select>
              </FormControl>

              {scheduleOption === 'specific' && (
                <>
                  <Controller
                    name="startDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Start Date"
                        maxDate={endDate}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={!!errors.startDate}
                            helperText={errors.startDate?.message}
                          />
                        )}
                        {...field}
                      />
                    )}
                  />
                  <Controller
                    name="endDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="End Date"
                        minDate={startDate}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={!!errors.endDate}
                            helperText={errors.endDate?.message}
                          />
                        )}
                        {...field}
                      />
                    )}
                  />
                </>
              )}
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: '400', textTransform: 'uppercase' }}
              >
                Location Options
              </Typography>

              <FormControl fullWidth>
                <InputLabel id="locationLabel">Location Options</InputLabel>
                <Select
                  labelId="locationLabel"
                  id="hasLocation"
                  error={!!errors.address}
                  fullWidth
                  label="Location Options"
                  value={hasLocation}
                  onChange={(e) => {
                    setHasLocation(e.target.value);
                    if (e.target.value == 'virtual') setValue('address', '');
                  }}
                >
                  <ListSubheader
                    sx={{
                      borderBottom: '1px solid #DDDDDD ',
                      fontWeight: '400',
                      color: '#B3B3B3'
                    }}
                  >
                    LOCATION OPTIONS
                  </ListSubheader>
                  <MenuItem value={'physical'}>Create new location</MenuItem>
                  <MenuItem value={'virtual'}>No location/virtual</MenuItem>
                </Select>
              </FormControl>
              <LocationInput
                error={!!errors.address}
                id="location"
                label="Address"
                placeholder={
                  hasLocation !== 'physical' ? 'None' : 'Insert a address'
                }
                disabled={hasLocation !== 'physical'}
                value={address}
                name={''}
                onChangeLocation={(address) => setValue('address', address)}
              />
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: '400', textTransform: 'uppercase' }}
                >
                  Description
                </Typography>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      id="description"
                      placeholder="Insert description..."
                      multiline
                      error={!!errors.description}
                      rows={3}
                      inputProps={{
                        maxLength: 200
                      }}
                      {...field}
                    />
                  )}
                />
              </Box>
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: '400', textTransform: 'uppercase' }}
                >
                  SPECIAL INSTRUCTIONS
                </Typography>
                <Controller
                  name="specialInstructions"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      id="specialInstructions"
                      multiline
                      placeholder="Insert special instructions..."
                      error={!!errors.specialInstructions}
                      rows={3}
                      inputProps={{
                        maxLength: 200
                      }}
                      {...field}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid
              item
              container
              flexDirection="column"
              justifyContent="space-between"
              gap={3}
              xs={12}
              md={6}
            >
              <Box display="flex" flexDirection="column" gap={3}>
                <Controller
                  name="talent"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Autocomplete
                      options={talents}
                      getOptionLabel={(option) =>
                        option.firstName + ' ' + option.lastName
                      }
                      renderOption={(props, option) => {
                        return (
                          <li {...props} key={option.id}>
                            {option.firstName + ' ' + option.lastName}
                          </li>
                        );
                      }}
                      filterSelectedOptions
                      value={value}
                      onChange={(e, data) => onChange(data)}
                      renderInput={(params) => (
                        <TextField
                          error={!!errors.talent}
                          helperText={errors.talent?.message}
                          {...params}
                          label="Select Talent *"
                        />
                      )}
                    />
                  )}
                />
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: '400', textTransform: 'uppercase' }}
                >
                  Payment Options
                </Typography>
                <Controller
                  name="paymentType"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <FormControl fullWidth>
                      <InputLabel id="paymentLabel">Payment Type *</InputLabel>
                      <Select
                        labelId="paymentLabel"
                        id="paymentType"
                        fullWidth
                        label="Payment Type *"
                        error={!!errors.paymentType}
                        value={value}
                        onChange={(data) => {
                          clearErrors('pricePerHour');
                          if (data.target.value === 'FLAT_FEE')
                            setValue('pricePerHour', null);
                          onChange(data.target.value);
                        }}
                      >
                        <ListSubheader
                          sx={{
                            borderBottom: '1px solid #DDDDDD ',
                            fontWeight: '400',
                            color: '#B3B3B3'
                          }}
                        >
                          PAYMENT TYPE
                        </ListSubheader>
                        <MenuItem value={'FLAT_FEE'}>Flat fee</MenuItem>
                        <MenuItem value={'HOURLY_RATE'}>Hourly rate</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
                {paymentType == 'HOURLY_RATE' && (
                  <TextField
                    id="pricePerHour"
                    placeholder={'Insert hourly rate'}
                    helperText={errors.pricePerHour?.message}
                    label="Hourly Rate *"
                    error={!!errors.pricePerHour}
                    value={pricePerHour}
                    onChange={(event) => {
                      setValue('pricePerHour', event.target.value ?? 0);
                      clearErrors('pricePerHour');
                    }}
                    InputProps={{
                      // eslint-disable-next-line
                      inputComponent: FormatCurrencyInput as any,
                      inputProps: {
                        maxLength: 8
                      }
                    }}
                  />
                )}
                <Box>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">
                      Does this project has a budget?
                    </FormLabel>
                    <RadioGroup
                      sx={{
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                      row
                      value={openBudget}
                      onChange={(e) => {
                        if (e.target.value === 'false') {
                          setValue('budget', '');
                        }
                        setOpenBudget(e.target.value === 'true');
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          pt: 2
                        }}
                      >
                        <FormControlLabel
                          value={true}
                          control={<Radio />}
                          label="Yes"
                        />

                        <TextField
                          disabled={!openBudget}
                          placeholder={openBudget ? 'Insert budget' : 'None'}
                          id="budget"
                          error={!!errors.budget}
                          value={budget}
                          onChange={(event) =>
                            setValue(
                              'budget',
                              event.target.value ? event.target.value : ''
                            )
                          }
                          InputProps={{
                            // eslint-disable-next-line
                            inputComponent: FormatCurrencyInput as any,
                            inputProps: {
                              maxLength: 11
                            }
                          }}
                        />
                      </Box>
                      {errors.budget && (
                        <FormHelperText sx={{ color: 'red' }} id="budget">
                          The budget for this project can not be below the
                          Amount Paid
                        </FormHelperText>
                      )}
                      <FormControlLabel
                        value={false}
                        control={<Radio />}
                        label="No"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
                {project?.status &&
                  ProjectStatus[project?.status] ===
                    ProjectStatus.IN_PROGRESS && (
                    <TextField
                      disabled
                      id="amount"
                      label="Amount Paid"
                      value={project?.amountPaid}
                    />
                  )}
                <Box>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">
                      Does this project request expire?
                    </FormLabel>
                    <RadioGroup
                      row
                      value={needExpire}
                      onChange={(e) => {
                        setNeedExpire(e.target.value === 'true');
                        setValue('expireDate', null);
                      }}
                    >
                      <FormControlLabel
                        value={true}
                        control={<Radio />}
                        label="Yes"
                      />
                      <FormControlLabel
                        value={false}
                        control={<Radio />}
                        label="No"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
                {needExpire && (
                  <>
                    <Controller
                      name="expireDate"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          label="Expire Date"
                          renderInput={(params) => <TextField {...params} />}
                          {...field}
                        />
                      )}
                    />
                  </>
                )}
                <Button
                  sx={{ width: 250 }}
                  variant="lightBlue"
                  fullWidth
                  onClick={() => {
                    projectAttachmentRef.current
                      ? projectAttachmentRef.current.click()
                      : null;
                  }}
                  startIcon={<Add />}
                >
                  Add attachments
                </Button>
                <Box sx={{ display: 'none' }}>
                  <input
                    type="file"
                    multiple
                    title="attachment"
                    ref={projectAttachmentRef}
                    onChange={(event) => onAttachFile(event.target.files)}
                  />
                </Box>
                <Box>
                  {attachments.map((attachment) => (
                    <AttachmentBox
                      key={attachment.id}
                      onClickFile={() => downloadAttachment(attachment.uuid)}
                      fileName={attachment.fileName}
                      onDeleteFile={() => onDeleteAttachment(attachment)}
                      onEditFile={(fileName) =>
                        onEditAttachment(attachment.uuid, fileName)
                      }
                    />
                  ))}
                  {files.map((file) => (
                    <AttachmentBox
                      key={file.name}
                      onClickFile={() => {
                        const href = URL.createObjectURL(file);
                        window.open(href, 'blank');
                      }}
                      fileName={file.name}
                      onDeleteFile={() => onDeleteFile(file)}
                      onEditFile={(fileName) => onEditFile(file, fileName)}
                    />
                  ))}
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  mt: 3
                }}
              >
                <Button disableElevation sx={{ mr: 4 }} onClick={saveAsDraft}>
                  {project?.status !== 'DRAFT'
                    ? 'Save as Draft'
                    : 'Update Draft'}
                </Button>
                <Button
                  onClick={() => {
                    if (
                      project?.status &&
                      ProjectStatus[project?.status] ===
                        ProjectStatus.IN_PROGRESS
                    ) {
                      setWarningModalOpen(true);
                    } else {
                      handleSubmit(onSubmit)();
                    }
                  }}
                  disabled={Object.keys(errors).length !== 0}
                  sx={{ px: 5 }}
                >
                  {editing && project?.status
                    ? submitButtonCondition(project?.status)
                    : 'Send Now'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </DetailsCard>
      <ConfirmModal
        submit={(reason) =>
          handleSubmit((data) => onSubmit({ ...data, editReason: reason }))()
        }
        setOpen={(e) => setWarningModalOpen(e)}
        open={warningModalOpen}
      />
      <SuccessProjectModal
        data={modalData}
        resetForm={() => {
          reset(defaultValues);
          setHasLocation('virtual');
          setNeedExpire(false);
          setScheduleOption('noSpecific');
          setFiles([]);
        }}
        editing={editing}
        open={successModalOpen}
        setOpen={(e) => setSuccessModalOpen(e)}
      />
    </>
  );
};

export default CreateProject;
